import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';

const iconBtnStyle = {
  width: 44, height: 44, borderRadius: '50%', border: 'none',
  background: 'rgba(255,255,255,0.15)', color: '#fff', cursor: 'pointer',
  fontSize: 22, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
};

const smallIconBtnStyle = {
  width: 36, height: 36, borderRadius: '50%', border: 'none',
  background: 'rgba(255,255,255,0.18)', color: '#fff', cursor: 'pointer',
  fontSize: 18, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
};

const arrowBtnStyle = {
  width: 52, height: 52, borderRadius: '50%', border: 'none',
  background: 'rgba(255,255,255,0.15)', color: '#fff', cursor: 'pointer',
  fontSize: 26, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
};

const ZOOM_MIN = 1;
const ZOOM_MAX = 5;
const ZOOM_STEP = 1.5;
const clampZoom = (z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z));

function PhotoLightbox({ images, startIndex = 0, onClose, onRotate }) {
  const [index, setIndex] = useState(startIndex);
  // Увеличение и сдвиг увеличенного снимка (перетаскиванием). Новый снимок — с начала
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const touchStartX = useRef(null);
  const drag = useRef(null);      // { x, y, ox, oy } — начало перетаскивания
  const pinch = useRef(null);     // { dist, zoom } — начало щипка двумя пальцами
  const imgRef = useRef(null);
  const movedRef = useRef(false); // было перетаскивание — click после него не закрывает просмотр

  useEffect(() => { setIndex(startIndex); }, [startIndex]);
  useEffect(() => { setZoom(1); setOffset({ x: 0, y: 0 }); }, [index]);

  // Сдвиг не дальше края увеличенного снимка
  const clampOffset = (o, z) => {
    const el = imgRef.current;
    if (!el || z <= 1) return { x: 0, y: 0 };
    const mx = (el.offsetWidth * (z - 1)) / 2;
    const my = (el.offsetHeight * (z - 1)) / 2;
    return { x: Math.min(mx, Math.max(-mx, o.x)), y: Math.min(my, Math.max(-my, o.y)) };
  };
  const applyZoom = (z) => {
    const nz = clampZoom(z);
    setZoom(nz);
    setOffset(o => clampOffset(o, nz));
  };
  const zoomIn = (e) => { e?.stopPropagation(); applyZoom(zoom * ZOOM_STEP); };
  const zoomOut = (e) => { e?.stopPropagation(); applyZoom(zoom / ZOOM_STEP); };
  const zoomReset = (e) => { e?.stopPropagation(); setZoom(1); setOffset({ x: 0, y: 0 }); };

  useEffect(() => {
    const onKey = (e) => {
      // Escape — только лайтбокс, окно под ним остаётся (перехват раньше Modal)
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); }
      else if (e.key === 'ArrowLeft') setIndex(i => (i - 1 + images.length) % images.length);
      else if (e.key === 'ArrowRight') setIndex(i => (i + 1) % images.length);
      else if (e.key === '+' || e.key === '=') setZoom(z => clampZoom(z * ZOOM_STEP));
      else if (e.key === '-' || e.key === '_') { setZoom(z => clampZoom(z / ZOOM_STEP)); setOffset({ x: 0, y: 0 }); }
      else if (e.key === '0') { setZoom(1); setOffset({ x: 0, y: 0 }); }
    };
    window.addEventListener('keydown', onKey, true);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = prevOverflow;
    };
  }, [images.length, onClose]);

  // Перетаскивание увеличенного снимка мышью: слушаем окно, чтобы не терять курсор за краем
  useEffect(() => {
    if (!dragging) return undefined;
    const move = (e) => {
      const d = drag.current;
      if (!d) return;
      if (Math.abs(e.clientX - d.x) + Math.abs(e.clientY - d.y) > 3) movedRef.current = true;
      setOffset(clampOffset({ x: d.ox + e.clientX - d.x, y: d.oy + e.clientY - d.y }, zoom));
    };
    const up = () => { drag.current = null; setDragging(false); };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragging, zoom]);

  if (!images || images.length === 0) return null;
  const cur = images[index];

  const next = (e) => { e?.stopPropagation(); setIndex(i => (i + 1) % images.length); };
  const prev = (e) => { e?.stopPropagation(); setIndex(i => (i - 1 + images.length) % images.length); };

  const touchDist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      pinch.current = { dist: touchDist(e.touches), zoom };
      touchStartX.current = null;
      return;
    }
    if (zoom > 1) {
      drag.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, ox: offset.x, oy: offset.y };
      touchStartX.current = null;
    } else {
      touchStartX.current = e.touches[0].clientX;
    }
  };
  const onTouchMove = (e) => {
    if (pinch.current && e.touches.length === 2) {
      applyZoom(pinch.current.zoom * (touchDist(e.touches) / pinch.current.dist));
    } else if (drag.current && e.touches.length === 1) {
      const d = drag.current;
      setOffset(clampOffset({ x: d.ox + e.touches[0].clientX - d.x, y: d.oy + e.touches[0].clientY - d.y }, zoom));
    }
  };
  const onTouchEnd = (e) => {
    pinch.current = null;
    drag.current = null;
    // Листание свайпом — только когда снимок не увеличен
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 50) {
      if (dx < 0) next();
      else prev();
    }
    touchStartX.current = null;
  };

  const onWheel = (e) => {
    e.stopPropagation();
    applyZoom(e.deltaY < 0 ? zoom * 1.2 : zoom / 1.2);
  };
  const onMouseDown = (e) => {
    if (zoom <= 1 || e.button !== 0) return;
    e.preventDefault();
    movedRef.current = false;
    drag.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
    setDragging(true);
  };


  return createPortal(
    <div
      onClick={() => {
        // отпустили мышь после перетаскивания за краем снимка — это не «закрыть»
        if (movedRef.current) { movedRef.current = false; return; }
        onClose();
      }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
        zIndex: 10000, display: 'flex', flexDirection: 'column',
        // щипок увеличивает снимок, а не всю страницу админки
        touchAction: 'none', overscrollBehavior: 'contain',
      }}
    >
      {/* Top bar: counter + close */}
      <div style={{
        flex: '0 0 auto', height: 64, display: 'flex', alignItems: 'center',
        justifyContent: 'center', position: 'relative', padding: '0 24px',
      }}>
        {images.length > 1 && (
          <div style={{
            color: '#fff', fontSize: 14, fontWeight: 500, padding: '6px 14px',
            background: 'rgba(0,0,0,0.5)', '--ch': '6px',
          }}>
            {index + 1} / {images.length}
          </div>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          title="Закрыть"
          style={{ ...iconBtnStyle, position: 'absolute', right: 24, top: '50%', transform: 'translateY(-50%)' }}
        >×</button>
      </div>

      {/* Image area */}
      <div onWheel={onWheel} style={{
        flex: '1 1 auto', minHeight: 0, position: 'relative', overflow: 'hidden',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '0 80px',
      }}>
        {images.length > 1 && (
          <>
            <button
              onClick={prev}
              title="Предыдущее"
              style={{ ...arrowBtnStyle, position: 'absolute', left: 24, top: '50%', transform: 'translateY(-50%)' }}
            >‹</button>
            <button
              onClick={next}
              title="Следующее"
              style={{ ...arrowBtnStyle, position: 'absolute', right: 24, top: '50%', transform: 'translateY(-50%)' }}
            >›</button>
          </>
        )}
        <img
          ref={imgRef}
          src={cur.src}
          alt=""
          draggable={false}
          onClick={(e) => e.stopPropagation()}
          onDoubleClick={(e) => { e.stopPropagation(); if (zoom > 1) zoomReset(); else applyZoom(2.5); }}
          onMouseDown={onMouseDown}
          style={{
            maxWidth: '100%', maxHeight: '100%', objectFit: 'contain',
            '--ch': '6px', boxShadow: '0 8px 32px rgba(0,0,0,0.4)', userSelect: 'none',
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
            transition: dragging || pinch.current ? 'none' : 'transform 0.15s ease-out',
            cursor: zoom > 1 ? (dragging ? 'grabbing' : 'grab') : 'zoom-in',
            position: 'relative', zIndex: 1,
          }}
        />
      </div>

      {/* Bottom bar: zoom + rotate */}
      <div style={{
        flex: '0 0 auto', height: 72, display: 'flex',
        alignItems: 'center', justifyContent: 'center', gap: 12,
      }}>
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: 'rgba(0,0,0,0.5)', '--ch': '6px', padding: '6px',
          }}
        >
          <button onClick={zoomOut} disabled={zoom <= ZOOM_MIN} title="Уменьшить (−)" aria-label="Уменьшить"
            style={{ ...smallIconBtnStyle, opacity: zoom <= ZOOM_MIN ? 0.4 : 1 }}>−</button>
          <button onClick={zoomReset} title="Исходный размер (0)" aria-label="Исходный размер"
            style={{ ...smallIconBtnStyle, width: 'auto', borderRadius: 18, padding: '0 10px', fontSize: 13, minWidth: 56 }}>
            {Math.round(zoom * 100)}%
          </button>
          <button onClick={zoomIn} disabled={zoom >= ZOOM_MAX} title="Увеличить (+)" aria-label="Увеличить"
            style={{ ...smallIconBtnStyle, opacity: zoom >= ZOOM_MAX ? 0.4 : 1 }}>+</button>
        </div>
        {onRotate && (
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              background: 'rgba(0,0,0,0.5)', '--ch': '6px', padding: '6px',
            }}
          >
            <button
              onClick={() => onRotate(cur, 'ccw')}
              title="Повернуть влево"
              style={smallIconBtnStyle}
            >↺</button>
            <button
              onClick={() => onRotate(cur, 'cw')}
              title="Повернуть вправо"
              style={smallIconBtnStyle}
            >↻</button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

export default PhotoLightbox;
